import { yupResolver } from "@hookform/resolvers/yup";
import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { ICellRendererParams } from "ag-grid-community";
import { SyntheticEvent, useCallback, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import CustomLoader from "../../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import { execBookingAction, IPendingListRow } from "../../../../../api/services";
import DialogCloseButton from "../../../../../components/DialogCloseButton";
import { FormAutocomplete, FormTextField } from "../../../../../components/fields";
import useDialog from "../../../../../hooks/useDialog";
import { BookingAction } from "../../../../../types";

interface IFormValues {
    subaction: string | null,
    note: string
}

const CellStatus = (params: ICellRendererParams<IPendingListRow>) => {

    const { t } = useTranslation('booking');

    const { dispatchGlobalData } = useContext(GlobalContext);

    const dialog = useDialog();

    const formDialog = useForm<IFormValues>({
        defaultValues: {
            subaction: null,
            note: ''
        },
        reValidateMode: 'onSubmit',
        resolver: yupResolver(
            yup
                .object({
                    subaction: yup
                        .string()
                        .nullable()
                        .when({
                            is: undefined,
                            then: schema => schema,
                            otherwise: schema => schema.required('message.validation.required'),
                        })
                })),
        shouldUnregister: true
    });

    const execBookingActionMutation = useMutation(
        execBookingAction,
        {
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            },
            onSuccess: () => {
                params.api.refreshServerSide({
                    purge: true
                });
            },
            onSettled: () => {
                dialog.handleClose()
            }
        }
    );

    const [action, setAction] = useState<BookingAction | null>(null);
    const [subactions, setSubactions] = useState<string[]>([]);
    const [hasNote, setHasNote] = useState<boolean>(false);
    const [dialogText, setDialogText] = useState<string | null>(null);
    const [isBlacklistAction, setIsBlacklistAction] = useState<boolean>(false);
    const [dialogTitle, setDialogTitle] = useState<string | null>(null);

    const value = useMemo<string>(() => (
        params.data ? `${params.data.booking.status}_${params.data.booking.subStatus}` : ''
    ), [params.data]);
    const options = useMemo(() => [
        value,
        ...(Object
            .keys(params.data?.permissions.actions ?? {})
            .filter(a => (
                a !== BookingAction.Edit &&
                a !== BookingAction.Book &&
                a !== BookingAction.AssignVehicle &&
                a !== BookingAction.Clone &&
                a !== BookingAction.CreateRA &&
                a !== BookingAction.resendMail &&
                a !== BookingAction.Cancel &&
                a !== BookingAction.cancelNew &&
                a !== BookingAction.hardEdit &&
                a !== BookingAction.CreateSubstitution &&
                a !== BookingAction.CreateRARecovery
            ))
        )
    ], [params.data, value]);

    const onChange = useCallback((e: SyntheticEvent, value: string) => {
        e.preventDefault()
        e.stopPropagation();
        setAction(value as BookingAction);
        const valueSubactions = params.data?.permissions.actions[value] ?? [];
        setSubactions(valueSubactions);
        if (valueSubactions.length > 0) {
            formDialog.register('subaction');
        }
        setIsBlacklistAction(false);
        switch (value) {
            case BookingAction.Confirm:
                setHasNote(false);
                setDialogText(t('message.info.confirm_booking_action'));
                setDialogTitle(t('label.statusChange'));
                break;
            case BookingAction.Reject:
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t('message.info.reject_booking_action'));
                setDialogTitle(t('label.statusChange'));
                break;
            case BookingAction.Cancel:
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t('message.info.cancel_booking_action'));
                setDialogTitle(t('label.statusChange'));
                break;
            case BookingAction.Fail:
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t('message.info.fail_booking_action'));
                setDialogTitle(t('label.statusChange'));
                break;
            case BookingAction.Available:
                setHasNote(false);
                setDialogText(t('message.info.available_booking_action'));
                setDialogTitle(t('label.statusChange'));
                break;
            case BookingAction.Unavailable:
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t('message.info.unavailable_booking_action'));
                setDialogTitle(t('label.statusChange'));
                break;
            case BookingAction.Blacklist:
                setIsBlacklistAction(true);
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t('message.info.blacklist_booking_action'));
                setDialogTitle(t('label.watchlistCheck'));
                break;
            default:
                return;
        }
        dialog.handleShow();
    }, [params.data, t]);
    const onClickConfirm = useCallback((data: IFormValues) => {
        if (
            !params.data ||
            !action
        ) {
            return;
        }
        execBookingActionMutation.mutate({
            bookingId: params.data.booking.id,
            action: action,
            subaction: data.subaction || undefined,
            note: data.note || undefined,
            lastUpdateTime: params.data.booking.requestUpdateDate
        });
    }, [
        params.data,
        action,
    ]);
    const onClickApprove = useCallback(() => {
        if (!params.data) {
            return;
        }
        execBookingActionMutation.mutate({
            bookingId: params.data.booking.id,
            action: BookingAction.Blacklist,
            subaction: 'APPROVE',
            note: formDialog.getValues('note') === '' ? undefined : formDialog.getValues('note'),
            lastUpdateTime: params.data.booking.requestUpdateDate
        });
    }, [params.data]);
    const onClickReject = useCallback(() => {
        if (!params.data) {
            return;
        }
        execBookingActionMutation.mutate({
            bookingId: params.data.booking.id,
            action: BookingAction.Blacklist,
            subaction: 'REJECTED',
            note: formDialog.getValues('note'),
            lastUpdateTime: params.data.booking.requestUpdateDate
        });
    }, [params.data]);

    return (
        <Box width="100%">
            <Autocomplete
                options={options}
                value={value}
                onChange={onChange}
                disableClearable
                blurOnSelect
                filterSelectedOptions
                selectOnFocus={false}
                getOptionLabel={(option) => t(`action.${option}`)}
                renderInput={(params) => <TextField {...params} />}
            />
            <Dialog
                open={dialog.open}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={dialog.handleClose} />
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialogText}
                    </DialogContentText>
                    {subactions.length > 0 &&
                        <FormAutocomplete<IFormValues>
                            name="subaction"
                            control={formDialog.control}
                            label={action === BookingAction.Cancel ? t('label.requestedBy') : t("label.reason")}
                            options={subactions}
                            getOptionLabel={option => t(`action.${option}`)}
                        />
                    }
                    {hasNote &&
                        <FormTextField<IFormValues>
                            name="note"
                            control={formDialog.control}
                            label={t('label.note')}
                        />
                    }
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={dialog.handleClose}
                    >
                        {t('label.back')}
                    </Button>
                    {isBlacklistAction ?
                        <>
                            <Button onClick={onClickApprove}>
                                {t('label.approve')}
                            </Button>
                            <Button onClick={formDialog.handleSubmit(onClickReject)}>
                                {t('label.reject')}
                            </Button>
                        </> :
                        <Button onClick={formDialog.handleSubmit(onClickConfirm)}>
                            {t('label.save')}
                        </Button>
                    }
                </DialogActions>
            </Dialog>
            <CustomLoader loading={execBookingActionMutation.isLoading} />
        </Box>
    );
};

export default CellStatus;
